import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";

// Constants
const PAGE_ID = 9;
const BG_ID = 9;
const BG_TYPE = "jpg";
// const NEXT_PAGE_ID = PAGE_ID + 1;
const BACK_PAGE_ID = PAGE_ID - 1;
// const LAST_PAGE = true;

const LEFT_TITLE = "Optimizing Operations with AI";

// Image slider component
const ImageSlider = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    setIsVisible(false); // Reset visibility when page changes
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage]); // This effect will run on currentPage changes

  const images = [1, 2, 3];
  const GAP = "calc(var(--width-73) * -1)";

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: "50%",
        right: 0,
        transform: "translateY(-50%)",
        width: "60%",
        height: "auto",
        pointerEvents: "none",
        zIndex: 10,
        overflow: "hidden",
      }}
    >
      {images.map((index, i) => (
        <div
          key={index}
          style={{
            marginTop: i === 0 ? "var(--width-50)" : 0,
            marginBottom: i < images.length - 1 ? GAP : 0,
            width: "100%",
            opacity: isVisible ? 1 : 0,
            transform: `translateX(${isVisible ? 0 : "100%"})`,
            transition: `opacity 0.7s ease-out, transform 0.7s ease-out`,
            transitionDelay: `${i * 0.3}s`,
          }}
        >
          <img
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_${index}.png`}
            alt={`Presentation Data ${index}`}
            style={{
              width: "75%",
              height: "auto",
              display: "block",
              animation: `glow 3s ease-in-out infinite`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  const backButton =
    BACK_PAGE_ID > 0
      ? {
          label: "Back",
          type: "back" as const,
          startIcon: { icon: "arrow-right", color: "#fff" },
          onClick: () => setCurrentPage(BACK_PAGE_ID),
          style: {
            borderRadius: "45px",
            padding: "var(--width-5) var(--width-25)",
            gap: "var(--width-16)",
          },
        }
      : undefined;

  const nextButton = {
    label: "Finish",
    type: "next" as const,
    onClick: () => setCurrentPage(1),
    style: {
      borderRadius: "45px",
      padding: "var(--width-5) var(--width-25)",
      gap: "var(--width-16)",
    },
  };

  return {
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${BG_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",

    title: LEFT_TITLE,
    description:
      "AI-driven automation transforms telco operations by\neliminating inefficiencies in manual tracking, reducing\noperational costs, and enhancing service delivery. Faster\nresolutions, proactive customer updates, and optimized\nworkforce management improve overall efficiency and\ncustomer satisfaction, leading to higher NPS scores and\nreduced call center volumes.",
    rightSideComponent: <ImageSlider key={currentPage} />,
    next: nextButton,
    back: backButton,
  };
};

export default DATA;
